import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import UserMenu from './UserMenu.jsx';
import { signup as signupApi, signInWithGoogle } from '../api/auth';
import { persistPendingSearch } from '../utils/guestSearch';
import { buildConsentPayload, persistPendingConsent } from '../utils/legal';
import LegalFooter from './LegalFooter.jsx';
import '../styles/mainPage.css';
import '../styles/legal.css';
import { useAuth } from './AuthContext.jsx';
import PageLoader from './PageLoader.jsx';

const SignupPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const navigate = useNavigate();
    const { isLoggedIn, loading: authLoading, isAuthBusy } = useAuth();

    useEffect(() => {
        if (!authLoading && !isAuthBusy && isLoggedIn) {
            navigate('/', { replace: true });
        }
    }, [authLoading, isAuthBusy, isLoggedIn, navigate]);

    const handleSignup = async (e) => {
        e.preventDefault();
        if (!acceptedTerms) {
            setError('Accept Privacy and Terms to create an account.');
            return;
        }
        setError('');
        setLoading(true);
        try {
            persistPendingSearch();
            persistPendingConsent();
            await signupApi(email, password, { consent: buildConsentPayload() });
            setLoading(false);
            navigate('/signup-success', { state: { email } });
        } catch (err) {
            setLoading(false);
            setError(err.message || 'Signup failed');
        }
    };

    const handleGoogleSignup = async (e) => {
        e.preventDefault();
        if (!acceptedTerms) {
            setError('Accept Privacy and Terms to create an account.');
            return;
        }
        setError('');
        setGoogleLoading(true);
        try {
            persistPendingSearch();
            persistPendingConsent();
            await signInWithGoogle();
        } catch (err) {
            setGoogleLoading(false);
            setError(err.message || 'Google signup failed');
        }
    };

    if (authLoading || isAuthBusy || googleLoading) {
        return <PageLoader />;
    }

    return (
        <div className="container" style={{ minHeight: '100vh', position: 'relative', background: 'transparent' }}>
            {/* Header */}
            <header style={{ position: 'fixed', top: 0, left: 0, right: 0, padding: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 20, background: 'transparent' }}>
                <span style={{ fontWeight: 700, fontSize: '1.5rem', color: '#fff', letterSpacing: '-1px', cursor: 'pointer' }} onClick={() => navigate('/')}>LiveNow</span>
                <nav style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    {isLoggedIn ? (
                        <UserMenu />
                    ) : null}
                </nav>
            </header>
            <div style={{ width: '100%', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <form onSubmit={handleSignup} autoComplete="off" style={{ width: '100%', maxWidth: 600, background: 'rgba(15,23,42,0.65)', borderRadius: 28, padding: '2.2rem 2.2rem', boxShadow: '0 8px 32px rgba(0,0,0,0.13)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18 }}>
                    <h2 style={{ color: '#fff', fontWeight: 900, fontSize: '2.2rem', marginBottom: 2, textAlign: 'center', letterSpacing: '-1px', lineHeight: 1.1 }}>Unlock the world of live content</h2>
                    <p style={{ color: '#b6c6e3', fontSize: '0.98rem', marginBottom: 14, textAlign: 'center', fontWeight: 400, maxWidth: 520, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.3 }}>
                        You're one click away from catching every live moment
                    </p>
                    <button onClick={handleGoogleSignup} type="button" disabled={googleLoading || !acceptedTerms} style={{
                        background: '#fff',
                        color: '#222',
                        border: 'none',
                        borderRadius: 999,
                        padding: '0.6rem 1.1rem',
                        fontSize: '0.98rem',
                        fontWeight: 600,
                        cursor: (googleLoading || !acceptedTerms) ? 'not-allowed' : 'pointer',
                        boxShadow: '0 2px 8px 0 rgba(0,0,0,0.06)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 10,
                        marginBottom: 10,
                        maxWidth: 360,
                        width: '100%',
                        marginLeft: 'auto',
                        marginRight: 'auto',
                        opacity: (googleLoading || !acceptedTerms) ? 0.7 : 1
                    }}>
                        <svg width="20" height="20" viewBox="0 0 20 20" style={{ marginRight: 8 }} xmlns="http://www.w3.org/2000/svg"><g><path d="M19.6 10.23c0-.68-.06-1.36-.18-2.02H10v3.82h5.44a4.64 4.64 0 01-2.01 3.05v2.54h3.24c1.9-1.75 2.99-4.33 2.99-7.39z" fill="#4285F4"/><path d="M10 20c2.7 0 4.97-.9 6.63-2.44l-3.24-2.54c-.9.6-2.05.96-3.39.96-2.6 0-4.8-1.76-5.59-4.13H1.07v2.59A10 10 0 0010 20z" fill="#34A853"/><path d="M4.41 11.85A5.99 5.99 0 014.09 10c0-.64.11-1.26.32-1.85V5.56H1.07A10 10 0 000 10c0 1.64.39 3.19 1.07 4.44l3.34-2.59z" fill="#FBBC05"/><path d="M10 4.04c1.47 0 2.78.51 3.81 1.5l2.86-2.86C14.97 1.13 12.7.04 10 .04A10 10 0 001.07 5.56l3.34 2.59C5.2 5.8 7.4 4.04 10 4.04z" fill="#EA4335"/></g></svg>
                        {googleLoading ? 'Connecting...' : 'Continue with Google'}
                    </button>
                    <div style={{ display: 'flex', alignItems: 'center', margin: '10px 0', width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
                        <div style={{ flex: 1, height: 1, background: '#2d3650', borderRadius: 2 }}></div>
                        <span style={{ margin: '0 12px', color: '#b6c6e3', fontSize: '0.92rem' }}>or</span>
                        <div style={{ flex: 1, height: 1, background: '#2d3650', borderRadius: 2 }}></div>
                    </div>
                    <input
                        type="email"
                        name="email"
                        placeholder="Email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        className="auth-input"
                        autoComplete="email"
                        required
                    />
                    <input
                        type="password"
                        name="password"
                        placeholder="Password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        className="auth-input"
                        autoComplete="new-password"
                        required
                    />
                    <label className="legal-consent">
                        <input
                            type="checkbox"
                            checked={acceptedTerms}
                            onChange={(e) => setAcceptedTerms(e.target.checked)}
                            required
                        />
                        <span>
                            I agree to the <Link to="/privacy">Privacy Policy</Link> and <Link to="/terms">Terms of Service</Link>.
                        </span>
                    </label>
                    {error && (
                        <div style={{ background: 'rgba(30,41,59,0.95)', color: '#fff', border: '1.5px solid #2563eb', borderRadius: 10, padding: '0.6rem 1rem', margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.97rem', fontWeight: 500, boxShadow: '0 2px 8px 0 rgba(37,99,235,0.08)', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                            {error}
                        </div>
                    )}
                    <button type="submit" className="livenow-signup-btn" style={{ marginTop: 6, fontSize: '0.97rem', padding: '0.6rem 1.1rem', maxWidth: 360, width: '100%', marginLeft: 'auto', marginRight: 'auto' }} disabled={loading || !acceptedTerms}>{loading ? 'Signing up...' : 'Sign up'}</button>
                    <div style={{ textAlign: 'center', marginTop: 8 }}>
                        <span style={{ color: '#b6c6e3', fontSize: '0.93rem' }}>Already have an account? </span>
                        <Link to="/login" style={{ color: '#3b82f6', fontWeight: 600, textDecoration: 'none', fontSize: '0.93rem' }}>Log in</Link>
                    </div>
                    <div style={{ textAlign: 'center', marginTop: 6 }}>
                        <Link to="/" style={{ color: '#b6c6e3', fontSize: '0.92rem', textDecoration: 'underline' }}>Back to Home</Link>
                    </div>
                </form>
            </div>
            <LegalFooter />
        </div>
    );
};

export default SignupPage; 